"use client";

import { useState, useRef, useEffect } from "react";
import Image from "next/image";
import { useAuth } from "@/contexts/AuthContext";
import { usePathname } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import { useTransition } from "react";
import { type MemberClubData } from "@/types/user";
import LoginLink from "@/components/LoginLink";
import { memberClubCardTierBadgeBackground } from "@/lib/member-club-tier-style";

export default function HeaderClient({ memberClubData }: { memberClubData?: MemberClubData | null }) {
  const pathname = usePathname();
  const { user } = useAuth();
  const locale = useLocale();
  const t = useTranslations("nav");
  const tCommon = useTranslations("common");
  const tMember = useTranslations("membership");
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isCoursesMenuOpen, setIsCoursesMenuOpen] = useState(false);
  const [isKnowledgeMenuOpen, setIsKnowledgeMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileCoursesOpen, setIsMobileCoursesOpen] = useState(false);
  const [isMobileKnowledgeOpen, setIsMobileKnowledgeOpen] = useState(false);
  const [isPending, startTransition] = useTransition();
  const currentLang = (locale === "en" ? "EN" : "TH") as "TH" | "EN";
  const closeMenuTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const profileCloseTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const formatDate = (isoDate: string) => {
    const d = new Date(isoDate)
    const months = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec']
    return `${d.getUTCDate().toString().padStart(2, '0')}/${months[d.getUTCMonth()]}/${d.getUTCFullYear()}`
  }

  const clearCloseTimeout = () => {
    if (closeMenuTimeoutRef.current) {
      clearTimeout(closeMenuTimeoutRef.current);
      closeMenuTimeoutRef.current = null;
    }
  };

  const clearProfileCloseTimeout = () => {
    if (profileCloseTimeoutRef.current) {
      clearTimeout(profileCloseTimeoutRef.current);
      profileCloseTimeoutRef.current = null;
    }
  };

  const scheduleCloseProfileMenu = () => {
  };

  const openMenu = (submenuKey: "courses" | "knowledge") => {
    clearCloseTimeout();
    if (submenuKey === "courses") {
      setIsCoursesMenuOpen(true);
      setIsKnowledgeMenuOpen(false);
    } else {
      setIsKnowledgeMenuOpen(true);
      setIsCoursesMenuOpen(false);
    }
  };

  const scheduleCloseMenu = () => {
    clearCloseTimeout();
    closeMenuTimeoutRef.current = setTimeout(() => {
      setIsCoursesMenuOpen(false);
      setIsKnowledgeMenuOpen(false);
    }, 1000);
  };

  const switchLocale = (newLocale: "th" | "en") => {
    document.cookie = `NEXT_LOCALE=${newLocale};path=/;max-age=31536000;SameSite=Lax`;
    startTransition(() => {
      window.location.reload();
    });
  };

  const navItems = [
    { label: t("courses"), href: "/courses", hasSubmenu: true, submenuKey: "courses" },
    { label: t("center"), href: "/center/hsrc", hasSubmenu: false },
    { label: t("news"), href: "/newsactivity", hasSubmenu: false },
    { label: t("knowledge"), href: "/safetytips", hasSubmenu: true, submenuKey: "knowledge" },
    { label: t("about"), href: "/aboutus", hasSubmenu: false },
  ];

  const courseSubmenuItems = [
    { label: t("courseMotorcycle"), href: "/course/motorcycle" },
    { label: t("courseGroup"), href: "/course/group" },
    { label: t("courseCar"), href: "/course/automobile" },
  ];

  const knowledgeSubmenuItems = [
    { label: t("knowledgePdsa"), href: "/safetytips/safetyfirst" },
    { label: t("knowledgeBasicRiding"), href: "/safetytips/saferiding" },
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (isCoursesMenuOpen && !target.closest('.courses-menu-container')) {
        setIsCoursesMenuOpen(false);
      }
      if (isKnowledgeMenuOpen && !target.closest('.knowledge-menu-container')) {
        setIsKnowledgeMenuOpen(false);
      }
      if (isProfileOpen && !target.closest('.profile-menu-container')) {
        setIsProfileOpen(false);
      }
    };

    if (isCoursesMenuOpen || isKnowledgeMenuOpen || isProfileOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCoursesMenuOpen, isKnowledgeMenuOpen, isProfileOpen]);

  useEffect(() => {
    const handleScroll = () => {
      if (isCoursesMenuOpen) {
        setIsCoursesMenuOpen(false);
      }
      if (isKnowledgeMenuOpen) {
        setIsKnowledgeMenuOpen(false);
      }
    };

    if (isCoursesMenuOpen || isKnowledgeMenuOpen) {
      window.addEventListener('scroll', handleScroll);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isCoursesMenuOpen, isKnowledgeMenuOpen]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  return (
    <>
      <div className="w-full h-1 bg-honda-red"></div>
      <header className="w-full h-[90px] bg-white relative z-50 border-b border-gray-200 flex items-center overflow-hidden">
        <div className="hidden min-[1440px]:flex items-center gap-2 pl-6 flex-shrink-0">
          <a href="/" className="flex-shrink-0">
            <Image
              src="/images/logos/logo-honda.png"
              alt="Honda Logo"
              width={120}
              height={40}
              className="h-[40px] w-auto"
              priority
              unoptimized
            />
          </a>
          <a href="/" className="flex-shrink-0">
            <Image
              src="/images/logos/logo-honda2.png"
              alt="Honda Logo 2"
              width={120}
              height={40}
              className="h-[40px] w-auto"
              priority
              unoptimized
            />
          </a>
          <div className="h-16 w-px bg-gray-300 hidden min-[1440px]:block"></div>
        </div>

        <div className="min-[1440px]:hidden absolute left-0 top-1/2 -translate-y-1/2 flex items-center gap-2 pl-6">
          <a href="/" className="flex-shrink-0">
            <Image
              src="/images/logos/logo-honda.png"
              alt="Honda Logo"
              width={120}
              height={40}
              className="h-[40px] w-auto"
              priority
              unoptimized
            />
          </a>
          <a href="/" className="flex-shrink-0">
            <Image
              src="/images/logos/logo-honda2.png"
              alt="Honda Logo 2"
              width={120}
              height={40}
              className="h-[40px] w-auto"
              priority
              unoptimized
            />
          </a>
        </div>

      <div className="flex-1 h-full flex items-center justify-between pl-6 pr-0">
        <div className="min-[1440px]:hidden flex-1" />

        <nav className="hidden min-[1440px]:flex items-center gap-2 min-[1440px]:gap-4">
          {navItems.map((item) => (
            item.hasSubmenu ? (
              <div
                key={item.href}
                className={`relative ${item.submenuKey === 'courses' ? 'courses-menu-container' : 'knowledge-menu-container'}`}
                onMouseEnter={() => openMenu(item.submenuKey as "courses" | "knowledge")}
                onMouseLeave={scheduleCloseMenu}
              >
                <button
                  onMouseEnter={() => openMenu(item.submenuKey as "courses" | "knowledge")}
                  onFocus={() => openMenu(item.submenuKey as "courses" | "knowledge")}
                  className="nav-link text-sm min-[1440px]:text-sm font-bold cursor-pointer bg-transparent border-none p-0 hover:text-dark whitespace-nowrap"
                >
                  {item.label}
                </button>
                {((item.submenuKey === 'courses' && isCoursesMenuOpen) || (item.submenuKey === 'knowledge' && isKnowledgeMenuOpen)) && (
                <div
                  className="fixed left-0 right-0 bg-gray-100 py-6 md:py-8 z-40 transition-all duration-300 ease-in-out opacity-100 translate-y-0 visible"
                  style={{ top: '90px' }}
                  onMouseEnter={clearCloseTimeout}
                >
                  <div className="max-w-[1920px] mx-auto px-6 min-[1440px]:px-6">
                    <div className="flex flex-col md:flex-row items-start md:items-center justify-center gap-6 md:gap-8 min-[1440px]:gap-10">
                      {(item.submenuKey === 'courses' ? courseSubmenuItems : knowledgeSubmenuItems).map((subItem) => (
                        <a
                          key={subItem.href}
                          href={subItem.href}
                          className="text-base md:text-lg min-[1440px]:text-xl text-dark hover:text-honda-red transition-colors cursor-pointer whitespace-nowrap font-bold"
                        >
                          {subItem.label}
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
                )}
              </div>
            ) : (
              <a key={item.href} href={item.href} className="nav-link text-sm min-[1440px]:text-sm font-bold whitespace-nowrap">
                {item.label}
              </a>
            )
          ))}
        </nav>

        <div className="flex items-center gap-1 ml-auto">
          <div className="hidden min-[1440px]:flex items-center gap-1 text-base">
            <button
              onClick={() => switchLocale("th")}
              disabled={isPending}
              className={`px-2 py-1 ${
                currentLang === "TH" ? "text-honda-red font-medium" : "text-dark opacity-30"
              }`}
            >
              TH
            </button>
            <span className="text-gray-300">|</span>
            <button
              onClick={() => switchLocale("en")}
              disabled={isPending}
              className={`px-2 py-1 ${
                currentLang === "EN" ? "text-honda-red font-medium" : "text-dark opacity-30"
              }`}
            >
              EN
            </button>
          </div>

          <div className="h-16 w-px bg-gray-300 hidden min-[1440px]:block ml-1" />

          <div className="hidden min-[1440px]:flex items-center gap-1 min-[1440px]:gap-2 ml-1 mr-0">
            <a href="https://www.facebook.com/HondaSafetyRidingThailand" target="_blank" rel="noopener noreferrer" className="w-6 h-6 min-[1440px]:w-7 min-[1440px]:h-7 rounded-full bg-honda-red flex items-center justify-center hover:bg-honda-red-dark transition-colors overflow-hidden">
              <Image
                src="/images/icons/icon-fb.svg"
                alt="Facebook"
                width={32}
                height={32}
                className="w-full h-full object-cover"
              />
            </a>
            <a href="https://www.tiktok.com/@hondasafetythailand" target="_blank" rel="noopener noreferrer" className="w-6 h-6 min-[1440px]:w-7 min-[1440px]:h-7 rounded-full bg-honda-red flex items-center justify-center hover:bg-honda-red-dark transition-colors overflow-hidden">
              <Image
                src="/images/icons/icon-tt.svg"
                alt="TikTok"
                width={32}
                height={32}
                className="w-full h-full object-cover"
              />
            </a>
          </div>

          <div className="h-16 w-px bg-gray-300 hidden min-[1440px]:block ml-1" />

          <div className="hidden md:flex items-center gap-2 ml-1 min-[1440px]:mr-1">
            <span className="text-base min-[1440px]:text-xl text-honda-red font-bold">02 757 6111 {tCommon('extLabel')} 3005</span>
            <Image
              src="/images/icons/icon-callcenter-red.svg"
              alt="Call Center"
              width={30}
              height={30}
              className="w-[20px] h-[20px] min-[1440px]:w-[24px] min-[1440px]:h-[24px]"
            />
            <div className="h-16 w-px bg-gray-300 ml-1 min-[1440px]:ml-0" />
          </div>

          {user ? (
            <div 
              className="profile-menu-container relative ml-2 md:ml-3 min-[1440px]:ml-0 min-[1440px]:mr-2"
            >
              <button
                onClick={() => setIsProfileOpen(!isProfileOpen)}
                className="group flex items-center gap-2 pl-1 pr-3 py-2 rounded-lg transition-colors"
              >
                <div className="w-8 h-8 rounded-full overflow-hidden flex items-center justify-center transition-colors">
                  <svg className="w-full h-full" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="32" cy="32" r="32" fill="transparent"/>
                    <g>
                      <path d="M36.93,28.1c0,2.72-2.21,4.93-4.93,4.93-.07,0-.15,0-.22,0-2.65-.22-4.69-2.42-4.71-5.08v-1.43c-.08-2.74,1.98-5.07,4.71-5.32,2.72-.12,5.03,1.98,5.15,4.7,0,.07,0,.15,0,.22v1.97Z" fill="none" stroke="#e41e26" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"/>
                      <circle cx="32" cy="32" r="16" fill="none" stroke="#e41e26" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"/>
                      <path d="M21.56,43.47c2.8-5.77,9.74-8.18,15.51-5.38,2.35,1.14,4.24,3.03,5.38,5.38" fill="none" stroke="#e41e26" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"/>
                    </g>
                  </svg>
                </div>
                <span className="text-xs text-dark hidden sm:inline">
                  {user.name || user.preferred_username || user.given_name || user.email?.split('@')[0] || tCommon("user")}
                </span>
                <svg
                  className={`w-4 h-4 text-honda-red transition-transform ${
                    isProfileOpen ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {isProfileOpen && (
                <div className="fixed right-0 w-56 bg-white rounded-lg shadow-lg overflow-hidden z-50" style={{ top: '90px' }}>
                  {memberClubData && (
                    <div className="bg-honda-red px-4 py-3 relative rounded-t-lg">
                      <div className="absolute top-3 left-4">
                        <div
                          className="px-2 py-1 rounded text-xs font-medium text-dark"
                          style={{
                            background: memberClubCardTierBadgeBackground(
                              memberClubData.memberClub.colorGradientFrom,
                              memberClubData.memberClub.colorGradientTo,
                              memberClubData.memberClub.name
                            ),
                          }}
                        >
                          {memberClubData.memberClub.name}
                        </div>
                      </div>
                      <div className="pt-8">
                        <p className="text-xs text-white">
                          {tMember("expiresIn", { days: memberClubData.expiresInDays })}
                        </p>
                        <p className="text-xs text-white mt-1">
                          {formatDate(memberClubData.expirationStart)} - {formatDate(memberClubData.expirationEnd)}
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="bg-white rounded-b-lg py-2">
                    <a href="/member/personal" className="block px-4 py-2 text-base hover:bg-gray-50">
                      {tCommon("profile")}
                    </a>
                    <a href="/member/course/history" className="block px-4 py-2 text-base hover:bg-gray-50">
                      {tCommon("purchaseHistory")}
                    </a>
                    <a
                      href={"/auth/logout?locale=" + locale}
                      className="w-full text-left px-4 py-2 text-base hover:bg-gray-50 text-honda-red block"
                    >
                      {tCommon("logout")}
                    </a>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="ml-2 md:ml-3 min-[1440px]:ml-0 min-[1440px]:mr-2">
              <LoginLink
                className="flex items-center gap-2 pl-1 pr-3 py-2 rounded-lg hover:bg-gray-100 transition-colors text-dark hover:text-honda-red"
              >
                <span className="text-xs sm:text-sm font-medium">{tCommon("login")}</span>
              </LoginLink>
            </div>
          )}

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="min-[1440px]:hidden flex items-center justify-center w-12 h-12 mr-2 text-honda-red hover:text-honda-red-dark transition-colors"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-10 h-8" fill="none" stroke="currentColor" viewBox="0 0 32 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h24M4 12h18M4 18h12" />
              </svg>
            )}
          </button>
        </div>
      </div>

      <>
        <div
          className={`fixed inset-0 bg-black/50 z-40 min-[1440px]:hidden transition-opacity duration-300 ease-in-out ${
            isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
          }`}
          onClick={() => setIsMobileMenuOpen(false)}
        />
        
        <div
          className={`fixed top-[90px] left-0 right-0 bg-white z-50 min-[1440px]:hidden transition-all duration-500 ease-in-out ${
            isMobileMenuOpen ? 'overflow-y-auto' : 'overflow-hidden'
          }`}
          style={{
            maxHeight: isMobileMenuOpen ? 'calc(100dvh - 90px)' : '0px',
            opacity: isMobileMenuOpen ? 1 : 0,
          }}
        >
            <div className="px-6 py-4">
              {navItems.map((item) => (
                <div key={item.href} className="border-b border-gray-200">
                  {item.hasSubmenu ? (
                    <div>
                      <button
                        onClick={() => {
                          if (item.submenuKey === 'courses') {
                            setIsMobileCoursesOpen(!isMobileCoursesOpen);
                            setIsMobileKnowledgeOpen(false);
                          } else if (item.submenuKey === 'knowledge') {
                            setIsMobileKnowledgeOpen(!isMobileKnowledgeOpen);
                            setIsMobileCoursesOpen(false);
                          }
                        }}
                        className="w-full flex items-center justify-between py-4 text-base font-bold text-dark hover:text-honda-red transition-colors"
                      >
                        <span>{item.label}</span>
                        <svg
                          className={`w-5 h-5 transition-transform ${
                            (item.submenuKey === 'courses' && isMobileCoursesOpen) || 
                            (item.submenuKey === 'knowledge' && isMobileKnowledgeOpen) 
                              ? "rotate-180" : ""
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      <div
                        className="grid transition-[grid-template-rows,opacity] duration-500 ease-in-out"
                        style={{
                          gridTemplateRows: ((item.submenuKey === 'courses' && isMobileCoursesOpen) || 
                            (item.submenuKey === 'knowledge' && isMobileKnowledgeOpen)) ? '1fr' : '0fr',
                          opacity: ((item.submenuKey === 'courses' && isMobileCoursesOpen) || 
                            (item.submenuKey === 'knowledge' && isMobileKnowledgeOpen)) ? 1 : 0,
                        }}
                      >
                        <div className="overflow-hidden">
                          <div className="pl-4 space-y-5 py-3">
                            {(item.submenuKey === 'courses' ? courseSubmenuItems : knowledgeSubmenuItems).map((subItem) => (
                              <a
                                key={subItem.href}
                                href={subItem.href}
                                onClick={() => setIsMobileMenuOpen(false)}
                                className="block text-sm text-gray-600 hover:text-honda-red transition-colors"
                              >
                                {subItem.label}
                              </a>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <a
                      href={item.href}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="block py-4 text-base font-bold text-dark hover:text-honda-red transition-colors"
                    >
                      {item.label}
                    </a>
                  )}
                </div>
              ))}
              
              <div className="border-b border-gray-200 py-4">
                <div className="flex items-center gap-1 text-base">
                  <button
                    onClick={() => switchLocale("th")}
                    disabled={isPending}
                    className={`px-2 py-1 ${
                      currentLang === "TH" ? "text-honda-red font-medium" : "text-dark opacity-30"
                    }`}
                  >
                    TH
                  </button>
                  <span className="text-gray-300">|</span>
                  <button
                    onClick={() => switchLocale("en")}
                    disabled={isPending}
                    className={`px-2 py-1 ${
                      currentLang === "EN" ? "text-honda-red font-medium" : "text-dark opacity-30"
                    }`}
                  >
                    EN
                  </button>
                </div>
              </div>

              <div className="border-b border-gray-200 py-4">
                <div className="flex items-center gap-4">
                  <a href="https://www.facebook.com/HondaSafetyRidingThailand" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-honda-red flex items-center justify-center hover:bg-honda-red-dark transition-colors overflow-hidden">
                    <Image
                      src="/images/icons/icon-fb.svg"
                      alt="Facebook"
                      width={32}
                      height={32}
                      className="w-full h-full object-cover"
                    />
                  </a>
                  <a href="https://www.tiktok.com/@hondasafetythailand" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-honda-red flex items-center justify-center hover:bg-honda-red-dark transition-colors overflow-hidden">
                    <Image
                      src="/images/icons/icon-tt.svg"
                      alt="TikTok"
                      width={32}
                      height={32}
                      className="w-full h-full object-cover"
                    />
                  </a>
                </div>
              </div>

              <div className="py-4 flex items-center gap-2">
                <a href="tel:027576111" className="text-base text-honda-red font-bold">02 757 6111 {tCommon('extLabel')} 3005</a>
                <Image
                  src="/images/icons/icon-callcenter-red.svg"
                  alt="Call Center"
                  width={30}
                  height={30}
                  className="w-[20px] h-[20px]"
                />
              </div>
            </div>
          </div>
      </>
    </header>
    </>
  );
}
